// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { Component } from "react";

import isEqual from "lodash-es/isEqual";
import PropTypes from "prop-types";

import { Header } from "components/elements";
import { Subsection, Accordion } from "components/layout";
import {
  IsVersionUpdated,
  HeaderContentWithUpdatedLabel,
} from "pages/Applications/AppDetails/AppVersions/_shared/IsVersionUpdated";
import { trans } from "src/translations";
import {
  CONTINENT_WORLD,
  getContinentNames,
  getCountries,
  getCountriesForContinent,
} from "utils/continents";

import styles from "./Markets.scss";

export class Markets extends Component {
  static propTypes = {
    simple: PropTypes.bool,
    selectedLocations: PropTypes.object.isRequired,
    countryCodesKey: PropTypes.oneOf([
      "app_designed_for",
      "app_is_available_on",
    ]),
  };

  static defaultProps = {
    simple: false,
  };

  static getHeaderText(region, howMany, from) {
    let header;

    if (howMany === from) {
      header = `${region} (${trans.ALL()})`;
    } else {
      header = `${region} (${howMany}/${from})`;
    }

    return header;
  }

  static renderCountryList(selectedCountries) {
    return (
      <ul className={styles.countryList}>
        {selectedCountries.map((country) => (
          <li key={country.name} className={styles.item}>
            {country.name}
          </li>
        ))}
      </ul>
    );
  }

  isSelected = (country) => {
    const { selectedLocations } = this.props;
    return selectedLocations.country_codes.includes(country.code);
  };

  isRegionUpdated = (allRegionCountries) => (activeVersion, latestVersion) => {
    const { countryCodesKey } = this.props;
    const allRegionCountryCodes = allRegionCountries.map((c) => c.code);

    const getCountryCodes = (version) => {
      return version.markets[countryCodesKey].country_codes;
    };

    const activeCountryCodes = getCountryCodes(activeVersion, countryCodesKey);
    const latestCountryCodes = getCountryCodes(latestVersion, countryCodesKey);

    const activeCountries = activeCountryCodes.filter((c) => {
      return allRegionCountryCodes.includes(c);
    });
    const latestCountries = latestCountryCodes.filter((c) => {
      return allRegionCountryCodes.includes(c);
    });

    return !isEqual(activeCountries.sort(), latestCountries.sort());
  };

  updatedMarkets = (region, allRegionCountries, selectedCountries) => (
    <IsVersionUpdated isUpdated={this.isRegionUpdated(allRegionCountries)}>
      {(isUpdated) => (
        <Accordion
          disabled={!selectedCountries.length}
          header={() => (
            <Header
              bold
              className={styles.countryHeader}
              color="primary"
              type="h4"
              uppercase={false}
            >
              <HeaderContentWithUpdatedLabel
                content={Markets.getHeaderText(
                  region,
                  selectedCountries.length,
                  allRegionCountries.length
                )}
                isUpdated={isUpdated}
              />
            </Header>
          )}
          longContent={region === CONTINENT_WORLD}
        >
          {Markets.renderCountryList(selectedCountries)}
        </Accordion>
      )}
    </IsVersionUpdated>
  );

  simpleMarkets = (region, allRegionCountries, selectedCountries) => (
    <Accordion
      disabled={!selectedCountries.length}
      header={Markets.getHeaderText(
        region,
        selectedCountries.length,
        allRegionCountries.length
      )}
      longContent={region === CONTINENT_WORLD}
    >
      {Markets.renderCountryList(selectedCountries)}
    </Accordion>
  );

  render() {
    const { simple } = this.props;

    return getContinentNames().map((region) => {
      const allRegionCountries = getCountriesForContinent(
        getCountries(),
        region
      );
      const selectedCountries = allRegionCountries.filter(this.isSelected);

      return (
        <Subsection key={region}>
          {simple
            ? this.simpleMarkets(region, allRegionCountries, selectedCountries)
            : this.updatedMarkets(
                region,
                allRegionCountries,
                selectedCountries
              )}
        </Subsection>
      );
    });
  }
}
